/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import cn from 'classnames';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SettingsIcon } from 'components/elements/Icons';
import { Participants, VideoCallSettings, VideoRoom, VideoChat, HeaderLogo } from 'components/modules';
import { dateUtils } from 'utils';
import { roomStates } from 'const';
import { HtmlRender } from 'components/elements';
import { useFetchVideoSessionEndMessage, useVideoRoomState, useVideoContext, useVideoCallWebSocket } from 'hooks';
import VideoSessionSettingsModal from './VideoSessionSettingsModal';

import Styles from './styles';

const useStyles = makeStyles(Styles);

function VideoSession({ expiresAt, participants, videoSessionId, appointmentId, accountId, twilioSessionToken, data }) {
  const classes = useStyles();
  const { t } = useTranslation('labels');
  const { id } = useParams();
  const { message: endCallMessage } = useFetchVideoSessionEndMessage(id, data?.videoSessionId);
  const [isVideoSettings, setIsVideoSettings] = useState(false);
  const { disconnect, reconnect, applySettings, isMobileChatOpened, setMobileChatOpened } = useVideoContext();
  const isPastVideoSession = useMemo(() => (expiresAt ? dateUtils.isDateBeforeNow(expiresAt) : false), [expiresAt]);
  const roomState = useVideoRoomState();
  useVideoCallWebSocket(appointmentId, videoSessionId, accountId);

  useEffect(() => {
    if (isPastVideoSession) disconnect();
  }, [isPastVideoSession]);

  return (
    <div className={classes.videoSession}>
      <div className={cn(classes.sessionWrapper, { [classes.mediumSessionWrapper]: roomState === roomStates.ended })}>
        {roomState === roomStates.ended && (
          <>
            <div className={classes.header}>
              <HeaderLogo />
            </div>
            <div className={classes.callEndMessage}>
              <Typography className={classes.roomHeader} variant="h5" component="h5">
                <HtmlRender data={endCallMessage} htmlProfile />
              </Typography>
            </div>
          </>
        )}

        {isVideoSettings && roomState === roomStates.connected && (
          <VideoSessionSettingsModal
            twilioSessionToken={twilioSessionToken}
            videoSessionId={videoSessionId}
            data={data}
            onRefresh={reconnect}
            onApply={applySettings}
            onClose={() => setIsVideoSettings(false)}
          />
        )}

        {roomState === roomStates.disconnected && (
          <>
            <Typography className={classes.roomHeader} variant="h5" component="h5">
              {t('video_session_waiting_room')}
            </Typography>
            <VideoCallSettings
              roomName={videoSessionId}
              token={twilioSessionToken}
              sessionToken={appointmentId}
              sessionName={data.sessionName}
              accountName={data.accountDisplayName}
              isWaitingRoom
            >
              <div className={classes.participants}>
                <Participants classes={classes.participantsWrapper} expectedParticipants={participants} accountId={data?.accountId} isWaitingRoom />
              </div>
            </VideoCallSettings>
          </>
        )}
        {(roomState === roomStates.connected || roomState === roomStates.reconnecting) && (
          <>
            <div className={classes.header}>
              <HeaderLogo />
              <button className={classes.settingsButton} type="button" onClick={() => setIsVideoSettings(true)}>
                <SettingsIcon classes={classes.settingsIcon} />
              </button>
            </div>
            <div className={cn(classes.roomWrapper, { [classes.mobileRoomWrapper]: isMobileChatOpened })}>
              <VideoRoom expectedParticipants={participants} onToggleMobileChat={() => setMobileChatOpened(!isMobileChatOpened)} oid={data?.organizationId} />

              <div className={cn(classes.chat, { [classes.activeMobileChat]: isMobileChatOpened })}>
                <Participants accountId={data?.accountId} classes={classes.chatParticipantsWrapper} expectedParticipants={participants} />
                <VideoChat
                  sessionToken={appointmentId}
                  sessionId={videoSessionId}
                  onClickBack={() => setMobileChatOpened(false)}
                  accountId={data?.accountId}
                  participants={participants}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

VideoSession.propTypes = {
  expiresAt: PropTypes.string,
  participants: PropTypes.arrayOf(PropTypes.any).isRequired,
  appointmentId: PropTypes.string,
  videoSessionId: PropTypes.string,
  accountId: PropTypes.string,
  twilioSessionToken: PropTypes.string,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

VideoSession.defaultProps = {
  expiresAt: '',
  appointmentId: '',
  videoSessionId: '',
  twilioSessionToken: '',
  accountId: '',
};

export default VideoSession;
